import React from 'react';
import classNames from 'classnames';

import Field, { hasDataInFields } from '../../Field';

import SectionContainer from '../SectionContainer';
import css from './SectionHighlights.module.css';

// Highlight icons are small, so we don't need large responsive variants
const ICON_IMAGE_SIZES = '32px';

/**
 * @typedef {Object} BlockConfig
 * @property {string} blockId
 * @property {string} blockName
 * @property {'defaultBlock'} blockType
 * @property {Object?} title
 * @property {Object?} text
 * @property {Object?} media image field used as the highlight icon
 */

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * Single highlight: an icon (coming from the block's media field) next to a short label.
 *
 * @component
 * @param {Object} props
 * @param {BlockConfig} props.block
 * @param {Object} props.options field options passed down to the Field renderer
 * @returns {JSX.Element|null}
 */
const Highlight = props => {
  const { block, options } = props;
  const { blockId, title, text, media } = block || {};

  if (!block) {
    return null;
  }

  const hasIcon = hasDataInFields([media], options);
  const hasLabel = hasDataInFields([title, text], options);

  if (!hasIcon && !hasLabel) {
    return null;
  }

  return (
    <li className={css.highlight} id={blockId}>
      {hasIcon ? (
        <div className={css.iconWrapper}>
          <Field data={media} sizes={ICON_IMAGE_SIZES} options={options} />
        </div>
      ) : null}
      {hasLabel ? (
        <div className={css.label}>
          <Field data={title} options={options} />
          <Field data={text} options={options} />
        </div>
      ) : null}
    </li>
  );
};

/**
 * Section that renders a horizontal bar of highlights (icon + short label).
 * All content (icons + labels) comes from the page asset data (blocks).
 * Each block's "media" field is used as the highlight icon.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.defaultClasses shared classes coming from SectionBuilder
 * @param {string} props.sectionId id of the section (matches the anchor id "highlights")
 * @param {Object?} props.title
 * @param {Object?} props.description
 * @param {Object?} props.appearance
 * @param {Object?} props.callToAction
 * @param {Array<BlockConfig>?} props.blocks array of block configs
 * @param {boolean?} props.isInsideContainer
 * @param {Object} props.options extra options for the section component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents custom fields
 * @returns {JSX.Element}
 */
const SectionHighlights = props => {
  const {
    sectionId,
    className,
    rootClassName,
    defaultClasses,
    title,
    description,
    appearance,
    callToAction,
    blocks = [],
    isInsideContainer = false,
    options,
  } = props;

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };

  const hasHeaderFields = hasDataInFields([title, description, callToAction], fieldOptions);
  const hasBlocks = blocks?.length > 0;

  return (
    <SectionContainer
      id={sectionId}
      className={classNames(className, css.section)}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
    >
      {hasHeaderFields ? (
        <header className={defaultClasses.sectionDetails}>
          <Field data={title} className={defaultClasses.title} options={fieldOptions} />
          <Field data={description} className={defaultClasses.description} options={fieldOptions} />
          <Field data={callToAction} className={defaultClasses.ctaButton} options={fieldOptions} />
        </header>
      ) : null}
      {hasBlocks ? (
        <ul
          className={classNames(css.highlights, {
            [css.noSidePaddings]: isInsideContainer,
          })}
        >
          {blocks.map((block, index) => (
            <Highlight
              key={block?.blockId || `highlight-${index}`}
              block={block}
              options={fieldOptions}
            />
          ))}
        </ul>
      ) : null}
    </SectionContainer>
  );
};

export default SectionHighlights;
